/**
 * Module dependencies.
 */

import { resolveAppUrl } from 'app/core/utils/url-resolver';
import { useMe } from './use-me';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRequestProvider } from 'app/components/providers/request-provider/request-provider';

/**
 * Export `FeatureFlag` type.
 */

export type FeatureFlag = {
  key: string;
  enabledForUsers: boolean;
  enabledForAdmins: boolean;
};

/**
 * Export `useFeatureFlags` — list every flag (admin only).
 */

export function useFeatureFlags() {
  const client = useRequestProvider();
  const { data: me } = useMe();

  return useQuery<FeatureFlag[]>(['adminFeatureFlags'], {
    // Only admins may read this endpoint — don't fire it for others (avoids a
    // spurious 403/error toast, e.g. when a non-admin hits the URL and is
    // being redirected away).
    enabled: !!me?.isAdmin,
    queryFn: async () =>
      client.get(resolveAppUrl('adminFeatureFlags')).then(({ data }: { data: any }) => data?.data ?? [])
  });
}

/**
 * Export `useUpdateFeatureFlag` — patch a flag's audience toggles (admin only).
 * Also invalidates `me`, since toggling can change the acting admin's own
 * effective features.
 */

export function useUpdateFeatureFlag() {
  const client = useRequestProvider();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      key,
      ...payload
    }: { key: string } & Partial<Omit<FeatureFlag, 'key'>>): Promise<FeatureFlag> =>
      client
        .patch(resolveAppUrl('adminFeatureFlagByKey', { interpolations: { key } }), payload)
        .then(({ data }: { data: any }) => data?.data),
    onSuccess: () => {
      queryClient.invalidateQueries(['adminFeatureFlags']);
      queryClient.invalidateQueries(['me']);
    }
  });
}

/**
 * Export `useFeature` — the effective on/off for the current user (from `/me`).
 * Defaults to `false` (hidden) until `me` has loaded, so disabled features never
 * flash in.
 */

export function useFeature(key: string): boolean {
  const { data: me } = useMe();

  return !!me?.features?.[key];
}
